import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, ArrowLeft, Tag } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }

    fetchProducts();
  }, [navigate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error carregant productes:', error);
      toast.error('Error carregant productes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Segur que vols eliminar aquest producte?')) {
      return;
    }

    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Producte eliminat');
      fetchProducts();
    } catch (error) {
      console.error('Error eliminant producte:', error);
      toast.error('Error eliminant producte');
    }
  };

  return (
    <div data-testid="admin-products-page" className="min-h-screen bg-muted/30 px-6 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" />
            Tornar al panell
          </Link>
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-4xl font-medium text-foreground">
              Productes
            </h1>
            <Link to="/admin/products/new">
              <Button data-testid="add-product-button" className="rounded-xl">
                <Plus className="h-5 w-5 mr-2" />
                Afegir Producte
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Carregant productes...</p>
          </div>
        ) : products.length === 0 ? (
          <div data-testid="no-products" className="text-center py-12">
            <p className="text-muted-foreground mb-6">No hi ha productes encara</p>
            <Link to="/admin/products/new">
              <Button>Afegir el primer producte</Button>
            </Link>
          </div>
        ) : (
          <div data-testid="products-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid={`admin-product-${product.id}`}
                className="bg-card rounded-2xl shadow-sm border border-border/50 p-6"
              >
                <div className="aspect-square rounded-xl overflow-hidden mb-4 bg-muted relative">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-contain"
                  />
                  {product.discount > 0 && (
                    <div className="absolute top-2 right-2 bg-accent text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium">
                      <Tag className="h-3 w-3" />
                      -{product.discount}%
                    </div>
                  )}
                </div>

                <h3 className="font-medium text-lg text-foreground mb-2">
                  {product.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-2">
                  {product.category}
                </p>
                <p className="text-lg font-semibold text-foreground mb-4">
                  {product.price.toFixed(2).replace('.', ',')} €
                </p>

                <div className="flex gap-2">
                  <Link to={`/admin/products/edit/${product.id}`} className="flex-1">
                    <Button 
                      variant="outline" 
                      className="w-full"
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Editar
                    </Button>
                  </Link>
                  <Button
                    variant="destructive"
                    onClick={() => handleDelete(product.id)}
                    data-testid={`delete-product-${product.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;